import { ActionReducer, createFeatureSelector, createSelector, MetaReducer } from '@ngrx/store';
import { routerReducer, RouterReducerState } from '@ngrx/router-store';
import { environment } from '../../environments/environment';
import { localStorageSync } from 'ngrx-store-localstorage';
import { BookedTrip } from '@ibe/models';
import { BookingStep } from '@ibe/models/ui';

/**
 * storeFreeze prevents state from being mutated. When mutation occurs, an
 * exception will be thrown. This is useful during development mode to
 * ensure that none of the reducers accidentally mutates the state.
 */
import { storeFreeze } from 'ngrx-store-freeze';

/**
 * Every reducer module's default export is the reducer function itself. In
 * addition, each module should export a type or interface that describes
 * the state of the reducer plus any selector functions. The `* as`
 * notation packages up all of the exports into a single object.
 */

import * as fromUser from './reducers/user.reducer';
import * as fromScreen from './reducers/screen.reducer';
import * as fromContacts from './reducers/contacts.reducer';
import * as fromCountryCodes from './reducers/country-code.reducer';
import * as fromBookedTrip from './reducers/booked-trip.reducer';
import * as fromBookingSteps from './reducers/booking-steps.reducer';
import * as fromOptionsBar from './reducers/options-bar.reducer';
import * as fromMonthSelectorBar from './reducers/month-selector-bar.reducer';
import * as fromTotalSummary from './reducers/booking-total-summary.reducer';
import * as fromFlightResult from './reducers/flight-result.reducer';
import * as fromFlightsFilter from './reducers/flights-filter.reducer';
import * as fromConfirmModal from './reducers/confirm-modal.reducer';
import * as fromPromoVouchers from './reducers/promo-vouchers.reducer';
// import * as fromMiniIbe from './reducers/mini-ibe.reducer';
import * as fromSeatmap from './reducers/seatmap.reducer';
import * as fromCalendar from './reducers/calendar.reducer';
import * as fromStopoverHotels from './reducers/stopover-hotels.reducer';
import * as fromMilesBar from './reducers/miles-bar.reducer';
import * as fromBestOffers from './reducers/best-offers.reducer';
import * as fromPaymentMethods from './reducers/payment-method.reducer';
import * as fromAncillariesWithMiles from './reducers/ancillaries-with-miles.reducer';
import * as fromModal from './reducers/modal.reducer';
import * as fromNotification from './reducers/notification.reducer';
import * as fromTripFinder from './reducers/trip-finder.reducer';

/* REPLICATED FILES */
import * as fromMiniIbe from './reducers/mini-ibe.indra.reducer';
import * as fromFlightFareInformation from './reducers/fare-information.indra.reducer';
import * as fromListOffers from './reducers/list-offers.reducer';

import * as fromCountryState from './reducers/country-state.reducer';
import { RouterStateUrl } from './states/router.state';
import { allEffects } from './effects';

import * as fromDeeplinkPriceMismatch from './reducers/deeplink-price-mismatch.reducer'
import * as fromFlightInformation from './reducers/flight-information.indra.reducer'
import * as fromOutboundFlightInformation from './reducers/outbound-flight-information.indra.reducer'
import * as fromInboundFlightInformation from './reducers/inbound-flight-information.indra.reducer'

import {
	LOCAL_STORAGE_VAR_KEY_ROUTE_NAME,
	LOCAL_STORAGE_VAR_KEY_DONT_REDIRECT,
} from '@ibe.config';

/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export class State {
	user: fromUser.State;
	screen: fromScreen.State;
	contacts: fromContacts.State;
	countryCodes: fromCountryCodes.State;
	seatmap: fromSeatmap.State;
	selectedBookedTrip: fromBookedTrip.State;
	selectedBookingSteps: fromBookingSteps.State;
	optionsBar: fromOptionsBar.State;
	monthSelectorBar: fromMonthSelectorBar.State;
	totalSummary: fromTotalSummary.State;
	flightResults: fromFlightResult.State;
	flightsFilter: fromFlightsFilter.State;
	confirmModal: fromConfirmModal.State;
	miniIBE: fromMiniIbe.State;
	calendar: fromCalendar.State;
	stopoverHotels: fromStopoverHotels.State;
	milesBar: fromMilesBar.State;
	best_offers: fromBestOffers.State;
	paymentMethods: fromPaymentMethods.State;
	flightFareInformation: fromFlightFareInformation.State;
	listOffers: fromListOffers.State;
	countryState: fromCountryState.State;
	router: RouterReducerState<RouterStateUrl>;
	ancillariesToPayWithMiles: fromAncillariesWithMiles.State;
	modal: fromModal.State;
	notification: fromNotification.State;
	promo_vouchers: fromPromoVouchers.State;
	tripFinder: fromTripFinder.State;
	priceMismatch: fromDeeplinkPriceMismatch.State;
	flightInformation: fromFlightInformation.State;
	outboundFlightInformation: fromOutboundFlightInformation.State;
	inboundFlightInformation: fromInboundFlightInformation.State;
}
/**
 * Local Store
 */
const syncStoreLocalStorage: Array<string> = [
	//'lang'
	// 'optionsBar', // should not be synced to local storage
	// 'flightsFilter', // should not be synced to local storage
	// 'user', // should not be synced to local storage
	// 'miniIBE', // should not be synced to local storage
	'user',
	'miniIBE',
	'selectedBookedTrip', 
	'selectedBookingSteps', 
	'milesBar',
	'ancillariesToPayWithMiles',
	'stopoverHotels',
	'listOffers',
	'flightInformation',
];

/**
 * Because metareducers take a reducer function and return a new reducer,
 * we can use our compose helper to chain them together. Here we are
 * using combineReducers to make our top level reducer, and then
 * wrapping that in storeLogger. Remember that compose applies
 * the result from right to left.
 */

export const reducers = {
	user: fromUser.reducer,
	screen: fromScreen.reducer,
	contacts: fromContacts.reducer,
	countryCodes: fromCountryCodes.reducer,
	seatmap: fromSeatmap.reducer,
	selectedBookedTrip: fromBookedTrip.reducer,
	selectedBookingSteps: fromBookingSteps.reducer,
	optionsBar: fromOptionsBar.reducer,
	monthSelectorBar: fromMonthSelectorBar.reducer,
	totalSummary: fromTotalSummary.reducer,
	flightResults: fromFlightResult.reducer,
	flightsFilter: fromFlightsFilter.reducer,
	confirmModal: fromConfirmModal.reducer,
	miniIBE: fromMiniIbe.reducer,
	calendar: fromCalendar.reducer,
	stopoverHotels: fromStopoverHotels.reducer,
	milesBar: fromMilesBar.reducer,
	best_offers: fromBestOffers.reducer,
	paymentMethods: fromPaymentMethods.reducer,
	flightFareInformation: fromFlightFareInformation.reducer,
	listOffers: fromListOffers.reducer,
	router: routerReducer,
	countryState: fromCountryState.reducer,
	ancillariesToPayWithMiles: fromAncillariesWithMiles.reducer,
	modal: fromModal.reducer,
	notification: fromNotification.reducer,
	promo_vouchers: fromPromoVouchers.reducer,
	tripFinder: fromTripFinder.reducer,
	priceMismatch: fromDeeplinkPriceMismatch.reducer,
	flightInformation: fromFlightInformation.flightInformationReducer,
	outboundFlightInformation: fromOutboundFlightInformation.outboundFlightInformationReducer,
	inboundFlightInformation: fromInboundFlightInformation.inboundFlightInformationReducer
};

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
	return (state, action) => {
		// For selected boooking steps, instantiate custom classes
		const newOrigBookingSteps: Array<BookingStep> = [];
		const newBookingSteps: Array<BookingStep> = [];
		let newBookedTrip: BookedTrip = null;
		if (state) {
			if (state.selectedBookingSteps) {
				if (
					state.selectedBookingSteps.orig_booking_steps.length && 
					!(state.selectedBookingSteps.orig_booking_steps.some(cls => cls instanceof BookingStep))
				) {
					const currentOrigBookingSteps = [...state.selectedBookingSteps.orig_booking_steps];
					currentOrigBookingSteps.forEach(step => newOrigBookingSteps.push(Object.assign(new BookingStep(null, null), step)));

					state = Object.assign(
						{},
						state,
						{
							selectedBookingSteps: {
								...state.selectedBookingSteps,
								orig_booking_steps: newOrigBookingSteps,
							},
						}
					);
				}

				if (
					state.selectedBookingSteps.booking_steps.length &&
					!(state.selectedBookingSteps.booking_steps.some(cls => cls instanceof BookingStep))
				) {
					const currentBookingSteps = [...state.selectedBookingSteps.booking_steps];
					currentBookingSteps.forEach(step => newBookingSteps.push(Object.assign(new BookingStep(null, null), step)));

					state = Object.assign(
						{},
						state,
						{
							selectedBookingSteps: {
								...state.selectedBookingSteps,
								booking_steps: newBookingSteps,
							}
						}
					);
				}
			}

			if (
				state.selectedBookedTrip &&
				state.selectedBookedTrip.bookedTrip &&
				!(state.selectedBookedTrip.bookedTrip instanceof BookedTrip)
			) {
				newBookedTrip = Object.assign(new BookedTrip(null, null, null), state.selectedBookedTrip.bookedTrip);

				state = Object.assign(
					{},
					state,
					{
						selectedBookedTrip: {
							...state.selectedBookedTrip,
							bookedTrip: newBookedTrip,
						},
					}
				);
			}
		}

		return localStorageSync({ keys: syncStoreLocalStorage, rehydrate: true, restoreDates: false })(reducer)(state, action);
	}
}

export const metaReducers: MetaReducer<State>[] = !environment.production
	? [storeFreeze, localStorageSyncReducer]
	: [localStorageSyncReducer];

/**
 * Effects (Middleware Processing)
 */
export const effects = allEffects;

/**
 * User Reducers
 */
export const getStateUser = createFeatureSelector<fromUser.State>('user');
export const getUserLoading = createSelector(getStateUser, fromUser.getLoading);
export const getLoggedIn = createSelector(getStateUser, fromUser.getLoggedIn);
export const getUserInfo = createSelector(getStateUser, fromUser.getUserInfo);
export const getUserPNRS = createSelector(getStateUser, fromUser.getUserPNRs);
export const getUserCountry = createSelector(getStateUser, fromUser.getUserCountry);
export const getUserMarket = createSelector(getStateUser, fromUser.getUserMarket);
export const getUserLanguage = createSelector(getStateUser, fromUser.getUserLanguage);
export const getFallbackHeader = createSelector(getStateUser, fromUser.getFallbackHeader);
export const getLoginCancel = createSelector(getStateUser, fromUser.getLoginCancel);
export const getConfirmDialog = createSelector(getStateUser, fromUser.getConfirmDialog);
export const getStateBestOffers = createFeatureSelector<fromBestOffers.State>('best_offers');
export const getBestOffers = createSelector(getStateBestOffers, fromBestOffers.getBestOffers);
export const getFlights = createSelector(getStateBestOffers, fromBestOffers.getFlights);
export const getJourneys = createSelector(getStateBestOffers, fromBestOffers.getJourneys);
export const getIsCorporate = createSelector(getStateUser, fromUser.getIsCorporate);
export const getExtendSession = createSelector(getStateUser, fromUser.getExtendSession);
export const getMarketLoading = createSelector(getStateUser, fromUser.getMarketLoading);
export const getUserDeeplinkMarket = createSelector(getStateUser, fromUser.getDeeplinkMarket);


/**
 * Screen Reducers
 */
export const getStateScreen = createFeatureSelector<fromScreen.State>('screen');
export const isScreenMobile = createSelector(getStateScreen, fromScreen.getMobile);
export const isScreenMobileTabletPortrait = createSelector(getStateScreen, fromScreen.getMobileTabletPortrait);
export const isScreenTabletPortrait = createSelector(getStateScreen, fromScreen.getTabletPortrait);
export const isScreenTabletLandscape = createSelector(getStateScreen, fromScreen.getTabletLandscape);
export const isScreenDesktop = createSelector(getStateScreen, fromScreen.getDesktop);

/**
 * Contacts Reducers
 */
export const getStateContacts = createFeatureSelector<fromContacts.State>('contacts');
export const getContactsLoading = createSelector(getStateContacts, fromContacts.getLoading);
export const getContacts = createSelector(getStateContacts, fromContacts.getContacts);

/**
 * Booked Reducers
 */
export const getStateSelectedBookedTrip = createFeatureSelector<fromBookedTrip.State>('selectedBookedTrip');
export const getSelectedBookedTripLoading = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getLoading);
export const getSelectedBookedTrip = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getBookedTrip);
export const getSelectedBookedTripDiscounts = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getBookedTripDiscounts);
export const getSelectedBookedTripPromoCodeVoucher = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getBookedTripPromoVouchers);
export const getSelectedBookedTripRecoverInsurance = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getBookedTripRecoverInsurance);
export const getSelectedBookedTripExtras = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getBookedTripExtras);
export const getSelectedBookedTripStopover = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getBookedTripStopover);
export const getSelectedBookedTripTimeToThink = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getBookedTripTTT);
export const getSelectedBookedTripTTTCompletionDetails = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getBookedTripTTTCompletionDetails);
export const getWeakFlightAmt = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getWeakFlightAmt);
export const getPreOrderEligibility = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getPreOrderElegibility);


export const getStateSelectedPromoVoucher = createFeatureSelector<fromBookedTrip.State>('promo_vouchers');
export const getSelectedPromoVouchers = createSelector(getStateSelectedPromoVoucher, fromPromoVouchers.getPromoVouchers);

/**
 * Booked Contacts
 */
export const getSelectedBookedTripContacts = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getContactDetails);
export const getSelectedBookedTripFlights = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getFlights);


export const getCountryCodesState = createFeatureSelector<fromCountryCodes.State>('countryCodes');
export const getCountryCodesLoading = createSelector(getCountryCodesState, fromCountryCodes.getLoading);
export const getCountryCodes = createSelector(getCountryCodesState, fromCountryCodes.getCountryCodes);
export const getCountryByCode = createSelector(fromCountryCodes.getCountryCodes, (state, code) => state.countryCodes.find(country => country.code == code));

export const getPaymentMethodsState = createFeatureSelector<fromPaymentMethods.State>('paymentMethods');
export const getPaymentMethodsLoading = createSelector(getPaymentMethodsState, fromPaymentMethods.getLoading);
export const getPaymentMethods = createSelector(getPaymentMethodsState, fromPaymentMethods.getPaymentMethods);

/**
 * Booked Flights
 */
export const getFlight = createSelector(getSelectedBookedTrip, (state, props) => state.flights.find(cur => cur.id == props.id));

/**
 * Booked Passengers
 */
export const getPassengers = createSelector(getStateSelectedBookedTrip, fromBookedTrip.getPassengers);
export const getPassenger = createSelector(getPassengers, (state, props) => { if (state) return state.find(cur => cur.id == props.id); });

/**
 * Booking Steps Reducers
 */
export const getStateSelectedBookingSteps = createFeatureSelector<fromBookingSteps.State>('selectedBookingSteps');
export const getSelectedBookingStepsLoading = createSelector(getStateSelectedBookingSteps, fromBookingSteps.getLoading);
export const getSelectedOrigBookingSteps = createSelector(getStateSelectedBookingSteps, fromBookingSteps.getOrigBookingSteps);
export const getSelectedBookingSteps = createSelector(getStateSelectedBookingSteps, fromBookingSteps.getBookingSteps);
export const getSelectedBookingCurrentStep = createSelector(getStateSelectedBookingSteps, fromBookingSteps.getBookingCurrentStep);
export const getSelectedBookingCurrentPage = createSelector(getStateSelectedBookingSteps, fromBookingSteps.getBookingCurrentPage);
export const getSelectedBookingType = createSelector(getStateSelectedBookingSteps, fromBookingSteps.getBookingType);
export const getBookingStepsCostsPerDay = createSelector(getStateSelectedBookingSteps, fromBookingSteps.getCostsPerDay);
export const getBookingStepsTTT = createSelector(getStateSelectedBookingSteps, fromBookingSteps.getTimeToThink);

/**
 * Options Bar component
 */
export const getOptionsBarState = createFeatureSelector<fromOptionsBar.State>('optionsBar');
export const getOptionsBarPriceUnit = createSelector(getOptionsBarState, fromOptionsBar.getPriceUnit);
export const getOptionsBarViewToShow = createSelector(getOptionsBarState, fromOptionsBar.getViewToShow);

/**
 * MiniIBE component
 */
export const getMiniIBEState = createFeatureSelector<fromMiniIbe.State>('miniIBE');
export const getMiniIBESearchRoutes = createSelector(getMiniIBEState, fromMiniIbe.getSearchRoutes);
export const getMiniIBEActiveInput = createSelector(getMiniIBEState, fromMiniIbe.getActiveInput);
export const getMiniIBECurrentSeachRoute = createSelector(getMiniIBEState, fromMiniIbe.getCurrentSearchRoute);
export const getMiniIBEActiveSearchRoute = createSelector(getMiniIBEState, fromMiniIbe.getCurrentRoute);
export const getMiniIBEPassengers = createSelector(getMiniIBEState, fromMiniIbe.getPassengers);
export const getMiniIBERouteType = createSelector(getMiniIBEState, fromMiniIbe.getRouteType);
export const getMiniIBEFlightType = createSelector(getMiniIBEState, fromMiniIbe.getFlightType);
export const getMiniIBEStopovers = createSelector(getMiniIBEState, fromMiniIbe.getStopovers);
export const getMiniIBERangeDates = createSelector(getMiniIBEState, fromMiniIbe.getRangeDates);
export const getMiniIBEDateOne = createSelector(getMiniIBEState, fromMiniIbe.getDateOne);
export const getMiniIBEIsStopOver = createSelector(getMiniIBEState, fromMiniIbe.getIsStopOver);
export const getMiniIBEDeeplinkParams = createSelector(getMiniIBEState, fromMiniIbe.getDeeplinkParams);

/**
 * Month Selector Bar
*/
export const getMonthSelectorBarState = createFeatureSelector<fromMonthSelectorBar.State>('monthSelectorBar');
export const getMonthSelectorBarMonth = createSelector(getMonthSelectorBarState, fromMonthSelectorBar.getSelectMonth);
export const getMonthSelectorBarStartDate = createSelector(getMonthSelectorBarState, fromMonthSelectorBar.getStartDate);

/**
 * Total Summary component
*/
export const getTotalSummaryState = createFeatureSelector<fromTotalSummary.State>('totalSummary');
export const getFlightSummary = createSelector(getTotalSummaryState, fromTotalSummary.getFlightsSummary);
export const getItensSummary = createSelector(getTotalSummaryState, fromTotalSummary.getItensSummary);
export const getTotalSymmary = createSelector(getTotalSummaryState, fromTotalSummary.getTotalSummary);

export const getFlightResultsState = createFeatureSelector<fromFlightResult.State>('flightResults');
export const getSelectedFlightFromFlightResults = createSelector(getFlightResultsState, fromFlightResult.getSelectedFlight);

export const getFlightsFilterState = createFeatureSelector<fromFlightsFilter.State>('flightsFilter');
export const getFlightsFilterDirectFlights = createSelector(getFlightsFilterState, fromFlightsFilter.getDirectFlights);
export const getFlightsFilterCabinOptions = createSelector(getFlightsFilterState, fromFlightsFilter.getCabinOptions);
export const getFlightsFilterBaggageIncluded = createSelector(getFlightsFilterState, fromFlightsFilter.getBaggageIncluded);
export const getFlightsFilterOnlyRefundable = createSelector(getFlightsFilterState, fromFlightsFilter.getOnlyRefundable);
export const getFlightsFilterSchedule = createSelector(getFlightsFilterState, fromFlightsFilter.getSchedule);
export const getFlightsFilterAirline = createSelector(getFlightsFilterState, fromFlightsFilter.getAirline);
export const getFlightsFilterStudentPassenger = createSelector(getFlightsFilterState, fromFlightsFilter.getStudent);
export const getFlightsFilterLiveAzoresMadeiraPassenger = createSelector(getFlightsFilterState, fromFlightsFilter.getLiveAzoresMadeira);
export const getFlightsFilterCountActiveItems = createSelector(getFlightsFilterState, fromFlightsFilter.countActiveItems);
export const getFlightsFilterBookAwardFlights = createSelector(getFlightsFilterState, fromFlightsFilter.getBookAwardFlights);
export const getFlightsFilterPayWithMilesEditableState = createSelector(getFlightsFilterState, fromFlightsFilter.getPayWithMilesEditableState);
export const getFlightsFilterStarAllianceFlights = createSelector(getFlightsFilterState, fromFlightsFilter.getStarAllianceFlights);
export const getFlightsFilterFlightWithMiles = createSelector(getFlightsFilterState, fromFlightsFilter.getFlightWithMiles);


export const getConfirmModalState = createFeatureSelector<fromConfirmModal.State>('confirmModal');
export const getConfirmModalTitle = createSelector(getConfirmModalState, fromConfirmModal.getTitle);
export const getConfirmModalBodyMsg = createSelector(getConfirmModalState, fromConfirmModal.getBodyMsg);
export const getConfirmModalOKLabel = createSelector(getConfirmModalState, fromConfirmModal.getOkLabel);
export const getConfirmModalCancelLabel = createSelector(getConfirmModalState, fromConfirmModal.getCancelLabel);
export const getConfirmModalConfirm = createSelector(getConfirmModalState, fromConfirmModal.getConfirm);
export const getConfirmModalIsAccent = createSelector(getConfirmModalState, fromConfirmModal.getIsAccent);

/**
 * Seatmap selector
 */
export const getSeatmapState = createFeatureSelector<fromSeatmap.State>('seatmap');
export const getSeatmapLoading = createSelector(getSeatmapState, fromSeatmap.getLoading);
export const getSeatmap = createSelector(getSeatmapState, fromSeatmap.getSeatmap);


/**
 * Calendar selector
 */
export const getCalendarState = createFeatureSelector<fromCalendar.State>('calendar');
export const getCalendarLoading = createSelector(getCalendarState, fromCalendar.getLoading);


/**
 * Stopover hotels
 */
export const getStopoversHotelsState = createFeatureSelector<fromStopoverHotels.State>('stopoverHotels');
export const getStopoversHotelsLoading = createSelector(getStopoversHotelsState, fromStopoverHotels.getLoading);
export const getStopoversHotels = createSelector(getStopoversHotelsState, fromStopoverHotels.getHotels);
export const getStopoversFeatureHotels = createSelector(getStopoversHotelsState, fromStopoverHotels.getFeatureHotels);
export const getStopoversHotelsFilters = createSelector(getStopoversHotelsState, fromStopoverHotels.getFilters);
export const getStopover = createSelector(getStopoversHotelsState, fromStopoverHotels.getStopover);
export const getSelectedCityId = createSelector(getStopoversHotelsState, fromStopoverHotels.getCityId);


/**
 * Miles bar selector
 */
export const getMilesBarState = createFeatureSelector<fromMilesBar.State>('milesBar');
export const getMilesBarDiscount = createSelector(getMilesBarState, fromMilesBar.getDiscount);
export const getMilesBarDiscountInformationMiles = createSelector(getMilesBarState, fromMilesBar.getDiscountInformationMiles);
export const getMilesBarHeight = createSelector(getMilesBarState, fromMilesBar.getHeight);


/**
 * Flight Fare Information selector
 */
export const getFlightFareInformationState = createFeatureSelector<fromFlightFareInformation.State>('flightFareInformation');
export const getFlightFareInformation =
	createSelector(getFlightFareInformationState, fromFlightFareInformation.getFlightFareInformation);

/**
 * List Offers selector
 */
export const getListOffersState = createFeatureSelector<fromListOffers.State>('listOffers');
export const getListOffers =
	createSelector(getListOffersState, fromListOffers.getListOffers);
	
/**
 * Router State
 */
export const getRouterStateSelector = createFeatureSelector<RouterReducerState<RouterStateUrl>>('router');
export const getRouterState = createSelector(getRouterStateSelector, state => state && state.state);

/**
 * Country State
 */
export const getCountryStateState = createFeatureSelector<fromCountryState.State>('countryState');
export const getSelectedCountryCodes = createSelector(getCountryStateState, fromCountryState.getCountryCodes);
export const getSelectedCountryCodesPrefixOpts = createSelector(getCountryStateState, fromCountryState.getCountryCodesPrefixOpts);
export const getSelectedCountryCodesOpts = createSelector(getCountryStateState, fromCountryState.getCountryCodesOpts);
export const getSelectedCountryConversionCodes = createSelector(getCountryStateState, fromCountryState.getCountryConversionCodes);
export const getSelectedCountryConversionCodesOpts = createSelector(getCountryStateState, fromCountryState.getCountryConversionCodesOpts);
export const getSelectedUSAirports = createSelector(getCountryStateState, fromCountryState.getUSAirports);
export const getSelectedUSStates = createSelector(getCountryStateState, fromCountryState.getUSStates);
export const getSelectedSchengenCountryCodes = createSelector(getCountryStateState, fromCountryState.getSchengenCountryCodes);
export const getSelectedEUCountryCodes = createSelector(getCountryStateState, fromCountryState.getEUCountryCodes);

/**
 * Ancillaries to Pay With Miles selector
 */
export const getAncillariesToPayWithMilesState = createFeatureSelector<fromAncillariesWithMiles.State>('ancillariesToPayWithMiles');
export const getAncillaryToPayWithMilesLoading = createSelector(getAncillariesToPayWithMilesState, fromAncillariesWithMiles.getLoading);
export const getAncillaryToPayWithMiles = createSelector(getAncillariesToPayWithMilesState, fromAncillariesWithMiles.getAncillaryToPay);
export const getAncillariesAddedToPayWithMiles = createSelector(getAncillariesToPayWithMilesState, fromAncillariesWithMiles.getAncillariesAdded);
export const getAncillaryTotalMilesToSpend = createSelector(getAncillariesToPayWithMilesState, fromAncillariesWithMiles.ancillaryTotalMilesToSpend);
export const getTotalMilesToSpend = createSelector(getAncillariesToPayWithMilesState, fromAncillariesWithMiles.totalMilesToSpend);
export const getAncillariesTotalDiscount = createSelector(getAncillariesToPayWithMilesState, fromAncillariesWithMiles.ancillariesTotalDiscount);
export const getMilesInFlightDiscount = createSelector(getAncillariesToPayWithMilesState, fromAncillariesWithMiles.getMilesInFlightDiscount);
export const getProcessAncillary = createSelector(getAncillariesToPayWithMilesState, fromAncillariesWithMiles.getProcessAncillary);
export const getAncillaryTotalToPay = createSelector(getAncillariesToPayWithMilesState, fromAncillariesWithMiles.getAncillaryTotalToPay);
export const getDiscountedBookingCost = createSelector(getAncillariesToPayWithMilesState, fromAncillariesWithMiles.getDiscountedBookingCost);
export const getOriginalBookingCost = createSelector(getAncillariesToPayWithMilesState, fromAncillariesWithMiles.getOriginalBookingCost);

/**
 * Modal component selector
 */
export const getModalState = createFeatureSelector<fromModal.State>('modal');
export const getShowClose = createSelector(getModalState, fromModal.getShowClose);

/**
 * Notification component selector
 */
export const getNotificationState = createFeatureSelector<fromNotification.State>('notification');
export const getNotificationTitle = createSelector(getNotificationState, fromNotification.getTitle);
export const getNotificationMensage = createSelector(getNotificationState, fromNotification.getMensage);

/**
 * Trip-Finder selector
 */
export const getTripFinderState = createFeatureSelector<fromTripFinder.State>('tripFinder');
export const getTripFinderLoading = createSelector(getTripFinderState, fromTripFinder.getLoading);
export const getTripFinderSearchParameters = createSelector(getTripFinderState, fromTripFinder.getSearchParameters);
export const getTripFinderOffers = createSelector(getTripFinderState, fromTripFinder.getOffers);
export const getTripFinderSelectedOffer = createSelector(getTripFinderState, fromTripFinder.getSelectedOffer);

export const getDeeplinkPriceMismatch = createFeatureSelector<fromDeeplinkPriceMismatch.State>('priceMismatch');
export const getFlightInformation = createFeatureSelector<fromFlightInformation.State>('flightInformation');
export const getOutboundFlightInformation = createFeatureSelector<fromOutboundFlightInformation.State>('outboundFlightInformation');
export const getInboundFlightInformation = createFeatureSelector<fromInboundFlightInformation.State>('inboundFlightInformation');
